"use client";
import React, { useEffect, useState } from "react";
import "@/src/components/Home/LandingV6/landing-v6.css";
import { Nav, StartWritingButton } from "@/src/components/Home/LandingV6/ui";
import { Footer } from "@/src/components/Home/LandingV6/blocks";
import { CornerPin, WaveRule, Asterism, Ribbon } from "@/src/components/Home/LandingV6/ornaments";

const SOURCE_DATE = "Aug 3, 2026";

const STATUS_META = {
  current: { color: "var(--pink)", label: "in Quarkle today" },
  legacy: { color: "var(--oat)", label: "legacy, still available" },
  reference: { color: "var(--muted)", label: "tops the board, not offered in Quarkle" },
};

// Pulled live from the leaderboards below on SOURCE_DATE. Grok 4.3 doesn't
// appear on either EQ-Bench board yet; that's a gap in the data, not a claim
// about quality, so it's shown with no bar rather than a fabricated one.
const PROSE_QUALITY = {
  question: "whose sentences actually sound good?",
  source: "EQ-Bench Creative Writing v3",
  sourceUrl: "https://eqbench.com/creative_writing.html",
  metric: "Rubric Score",
  totalModels: 116,
  axisMax: 100,
  note: "LLM-judged prose rubric (voice, plot craft, coherence). Judged by Claude Sonnet 4.6, worth knowing since Claude models are partly being scored by a Claude judge. Rank is by Rubric Score specifically, not the site's own default Elo sort, so it lines up with the bars above.",
  rows: [
    { name: "Claude Opus 5", status: "reference", value: 85.35, elo: 2429.5, rank: 1 },
    { name: "GPT-5.4", status: "legacy", value: 84.45, elo: 1885.9, rank: 3 },
    { name: "Kimi K2.6", status: "current", value: 83.35, elo: 1709.6, rank: 9, tag: "NSFW" },
    { name: "GPT-5.6 Terra", status: "current", value: 82.8, elo: 1886.5, rank: 15 },
    { name: "Claude Sonnet 4.6", status: "legacy", value: 82.5, elo: 1872.6, rank: 19 },
    { name: "Claude Sonnet 5", status: "current", value: 82.35, elo: 1781.8, rank: 20 },
    { name: "Gemini 3.1 Pro", status: "legacy", value: 80.2, elo: 1459.5, rank: 44 },
    { name: "Kimi K2.5", status: "current", value: 79.7, elo: 1576.9, rank: 51, tag: "NSFW" },
    { name: "Grok 4.3", status: "nodata", tag: "NSFW" },
  ],
};

const BRAINSTORMING = {
  question: "whose plans and plots hold up?",
  source: "EQ-Bench Longform Creative Writing",
  sourceUrl: "https://eqbench.com/creative_writing_longform.html",
  metric: "Score",
  totalModels: 125,
  axisMax: 100,
  note: "This benchmark's own method: brainstorm a plot from a minimal prompt, revise the plan, then write it out over 8 rounds of ~1,000 words. Closest direct test of brainstorming we found. Same judge caveat applies.",
  rows: [
    { name: "Claude Opus 5", status: "reference", value: 86.3, rank: 1 },
    { name: "Claude Sonnet 4.6", status: "legacy", value: 79.9, rank: 6 },
    { name: "Kimi K2.6", status: "current", value: 78.5, rank: 8, tag: "NSFW" },
    { name: "GPT-5.4", status: "legacy", value: 78.3, rank: 9 },
    { name: "Claude Sonnet 5", status: "current", value: 78.3, rank: 10 },
    { name: "GPT-5.6 Terra", status: "current", value: 78.0, rank: 12 },
    { name: "Kimi K2.5", status: "current", value: 74.9, rank: 17, tag: "NSFW" },
    { name: "Gemini 3.1 Pro", status: "legacy", value: 68.2, rank: 37 },
    { name: "Grok 4.3", status: "nodata", tag: "NSFW" },
  ],
};

const AI_SOUNDING = {
  question: "who overuses AI clichés the least?",
  source: "EQ-Bench Creative Writing v3: Slop Score",
  sourceUrl: "https://eqbench.com/creative_writing.html",
  metric: "Slop Score",
  totalModels: 116,
  axisMax: 32,
  lowerIsBetter: true,
  note: "Tallies words and constructions LLMs overuse (\"not just X, but Y\") against real human writing. Lower is better here: a shorter bar means fewer AI-isms, not a worse score.",
  rows: [
    { name: "Claude Opus 5", status: "reference", value: 6.59, rank: 1 },
    { name: "Claude Sonnet 4.6", status: "legacy", value: 9.9, rank: 3 },
    { name: "Claude Sonnet 5", status: "current", value: 11.6, rank: 9 },
    { name: "GPT-5.4", status: "legacy", value: 12.2, rank: 14 },
    { name: "GPT-5.6 Terra", status: "current", value: 12.4, rank: 15 },
    { name: "Kimi K2.6", status: "current", value: 13.3, rank: 19, tag: "NSFW" },
    { name: "Kimi K2.5", status: "current", value: 17.77, rank: 33, tag: "NSFW" },
    { name: "Gemini 3.1 Pro", status: "legacy", value: 30.03, rank: 66 },
    { name: "Grok 4.3", status: "nodata", tag: "NSFW" },
  ],
};

const CRITIQUE_JUDGING = {
  question: "can it tell good writing from bad?",
  source: "EQ-Bench Judgemark v4",
  sourceUrl: "https://eqbench.com/judgemark-v4.html",
  metric: "Judgemark Score",
  totalModels: 36,
  axisMax: 100,
  note: "Judgemark doesn't test editorial notes. It tests whether a model's own scoring of writing samples actually separates strong writing from weak. That's necessary for critique, not the same thing as giving useful notes, but it's the closest real benchmark we found, and the only one here with a Grok 4.3 result.",
  rows: [
    { name: "Claude Opus 4.6", status: "reference", value: 90.73, rank: 1 },
    { name: "Claude Sonnet 4.6", status: "legacy", value: 82.15, rank: 4 },
    { name: "Gemini 3.1 Pro", status: "legacy", value: 78.69, rank: 6 },
    { name: "GPT-5.4", status: "legacy", value: 72.08, rank: 11 },
    { name: "Claude Sonnet 5", status: "current", value: 70.89, rank: 12 },
    { name: "Kimi K2.6", status: "current", value: 57.26, rank: 18, tag: "NSFW" },
    { name: "Grok 4.3", status: "current", value: 49.57, rank: 23, tag: "NSFW" },
    { name: "GPT-5.6 Terra", status: "nodata" },
    { name: "Kimi K2.5", status: "nodata", tag: "NSFW" },
  ],
};

const SUSTAINED_QUALITY = {
  question: "whose quality holds up over a long chapter?",
  source: "EQ-Bench Longform Creative Writing: Degradation",
  sourceUrl: "https://eqbench.com/creative_writing_longform.html",
  metric: "Degradation Score",
  totalModels: 125,
  axisMax: 0.15,
  decimals: 3,
  lowerIsBetter: true,
  note: "Measures how much quality drops from a model's first chapter to its last, across an 8-chapter run. 0 means no measurable drop-off. Lower is better here: a shorter bar means it holds up better over length, not a worse score. No \"not offered\" reference bar on this one: Claude Sonnet 4.6 is already #1 in the full field.",
  rows: [
    { name: "Claude Sonnet 4.6", status: "legacy", value: 0.0, rank: 1 },
    { name: "Kimi K2.6", status: "current", value: 0.011, rank: 10, tag: "NSFW" },
    { name: "Claude Sonnet 5", status: "current", value: 0.014, rank: 11 },
    { name: "GPT-5.4", status: "legacy", value: 0.019, rank: 14 },
    { name: "GPT-5.6 Terra", status: "current", value: 0.036, rank: 15 },
    { name: "Kimi K2.5", status: "current", value: 0.07, rank: 22, tag: "NSFW" },
    { name: "Gemini 3.1 Pro", status: "legacy", value: 0.123, rank: 36 },
    { name: "Grok 4.3", status: "nodata", tag: "NSFW" },
  ],
};

// A fiction-specific needle-in-the-haystack eval: same story, comprehension
// questions asked at increasing context lengths. Only one of our 8 models
// has been tested on it; real numbers for that one, not a ranking.
const WORLD_BUILDING = {
  question: "does comprehension hold up at 192,000 tokens?",
  source: "Fiction.liveBench: Long Context Deep Comprehension",
  sourceUrl: "https://fiction.live/stories/Fiction-liveBench-Feb-21-2025/oQdzQvKHw8JyXbN87",
  metric: "Comprehension Score",
  rowLabel: "Context length",
  axisMax: 100,
  note: "Kimi K2.5 is the only one of our 8 models this eval has tested. Same story, comprehension questions asked at increasing context lengths. Real curve, not a cross-model ranking: this is the shape of the problem, for the one model we could verify.",
  rows: [
    { name: "0 tokens", status: "current", value: 100.0 },
    { name: "400 tokens", status: "current", value: 100.0 },
    { name: "1k tokens", status: "current", value: 100.0 },
    { name: "2k tokens", status: "current", value: 100.0 },
    { name: "4k tokens", status: "current", value: 94.4 },
    { name: "8k tokens", status: "current", value: 88.9 },
    { name: "16k tokens", status: "current", value: 86.1 },
    { name: "32k tokens", status: "current", value: 88.9 },
    { name: "60k tokens", status: "current", value: 88.9 },
    { name: "120k tokens", status: "current", value: 78.1 },
    { name: "192k tokens", status: "current", value: 87.5 },
  ],
};

// Real production usage, not a benchmark: every explicit model pick on a
// chat/brainstorm turn, Jul 24 – Aug 3 2026 (4,535 turns, 164 conversations).
// Excludes turns left on "auto" (routed by Quarkle, not chosen) and turns
// from before per-turn routing was recorded. Source: wss-agent-metrics.
const USAGE_SHARE = [
  { name: "Claude Sonnet 4.6", status: "legacy", pct: 36.6 },
  { name: "Kimi K2.6", status: "current", pct: 25.0, tag: "NSFW" },
  { name: "Grok 4.3", status: "current", pct: 13.6, tag: "NSFW" },
  { name: "Claude Sonnet 5", status: "current", pct: 9.0 },
  { name: "Kimi K2.5", status: "current", pct: 8.6, tag: "NSFW" },
  { name: "GPT-5.4", status: "legacy", pct: 3.7 },
  { name: "Gemini 3.1 Pro", status: "legacy", pct: 2.7 },
  { name: "GPT-5.6 Terra", status: "current", pct: 0.8 },
];

function Bar({ value, axisMax, color }) {
  const pct = Math.max(3, Math.min(100, (value / axisMax) * 100));
  return (
    <div
      style={{
        flex: 1,
        height: 10,
        borderRadius: 999,
        background: "color-mix(in oklab, var(--ink) 8%, transparent)",
        overflow: "hidden",
      }}
    >
      <div style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: color }} />
    </div>
  );
}

function Legend() {
  return (
    <div style={{ display: "flex", gap: 22, justifyContent: "center", flexWrap: "wrap", marginBottom: 40 }}>
      {["current", "legacy", "reference"].map((k) => (
        <div key={k} className="mono" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, opacity: 0.7 }}>
          <span aria-hidden style={{ width: 9, height: 9, borderRadius: 999, background: STATUS_META[k].color, flexShrink: 0 }} />
          {STATUS_META[k].label}
        </div>
      ))}
      <div className="mono" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, opacity: 0.7 }}>
        <span aria-hidden style={{ width: 9, height: 9, borderRadius: 999, border: "1.5px solid var(--rule)", flexShrink: 0 }} />
        not yet benchmarked
      </div>
    </div>
  );
}

function RankChart({ data }) {
  return (
    <div
      style={{
        background: "var(--paper-3)",
        border: "1.5px solid var(--ink)",
        borderRadius: 16,
        padding: "36px 40px",
        boxShadow: "6px 6px 0 color-mix(in oklab, var(--ink) 18%, transparent)",
      }}
    >
      <h3 className="tanker" style={{ fontSize: "clamp(24px, 3vw, 34px)", margin: "0 0 6px", lineHeight: 1.08 }}>
        {data.question}
      </h3>
      <p className="mono" style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 24px" }}>
        source:{" "}
        <a href={data.sourceUrl} target="_blank" rel="noreferrer" style={{ color: "inherit" }}>
          {data.source}
        </a>{" "}
        · fetched {SOURCE_DATE}
        {data.totalModels ? ` · of ${data.totalModels} models tested` : ""}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        {data.rows.map((r) => (
          <div
            key={r.name}
            style={{ display: "grid", gridTemplateColumns: "minmax(150px, 190px) 1fr 96px", alignItems: "center", gap: 14 }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: STATUS_META[r.status]?.color || "transparent", border: r.status === "nodata" ? "1.5px solid var(--rule)" : "none", flexShrink: 0 }} />
              <span className="mono" style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.name}
              </span>
              {r.tag && (
                <span className="mono" style={{ fontSize: 8, color: r.tagColor || "var(--clay)", border: `1px solid ${r.tagColor || "var(--clay)"}`, borderRadius: 999, padding: "1px 6px", flexShrink: 0 }}>
                  {r.tag}
                </span>
              )}
            </div>
            {r.status === "nodata" ? (
              <div className="mono" style={{ fontSize: 12, opacity: 0.45, fontStyle: "italic" }}>
                not on this leaderboard yet
              </div>
            ) : (
              <Bar value={r.value} axisMax={data.axisMax} color={STATUS_META[r.status].color} />
            )}
            <div style={{ textAlign: "right" }}>
              {r.status !== "nodata" && (
                <>
                  <span className="tanker" style={{ fontSize: 17 }}>
                    {r.value.toFixed(data.decimals ?? 1)}
                  </span>
                  {r.rank && (
                    <div className="mono" style={{ fontSize: 10, opacity: 0.45 }}>
                      #{r.rank}/{data.totalModels}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 13.5, lineHeight: 1.6, opacity: 0.7, margin: "24px 0 0" }}>{data.note}</p>

      <details style={{ marginTop: 18 }}>
        <summary className="mono" style={{ fontSize: 11, cursor: "pointer", opacity: 0.55, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          view as table
        </summary>
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--rule)" }}>
                <th style={{ textAlign: "left", padding: "6px 8px" }}>{data.rowLabel || "Model"}</th>
                <th style={{ textAlign: "right", padding: "6px 8px" }}>{data.metric}</th>
                {data.rows.some((r) => r.elo) && <th style={{ textAlign: "right", padding: "6px 8px" }}>Elo</th>}
                <th style={{ textAlign: "right", padding: "6px 8px" }}>Rank</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.name} style={{ borderBottom: "1px solid var(--rule)" }}>
                  <td style={{ padding: "6px 8px" }}>{r.name}</td>
                  <td style={{ textAlign: "right", padding: "6px 8px" }}>{r.status === "nodata" ? "-" : r.value.toFixed(data.decimals ?? 2)}</td>
                  {data.rows.some((row) => row.elo) && (
                    <td style={{ textAlign: "right", padding: "6px 8px" }}>{r.elo ? r.elo.toFixed(1) : "-"}</td>
                  )}
                  <td style={{ textAlign: "right", padding: "6px 8px" }}>{r.rank ? `#${r.rank}` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function UsageShareBar() {
  const sorted = [...USAGE_SHARE].sort((a, b) => b.pct - a.pct);
  const top = sorted[0];
  const second = sorted[1];
  return (
    <div
      style={{
        background: "var(--paper-3)",
        border: "1.5px solid var(--ink)",
        borderRadius: 16,
        padding: "36px 40px",
        boxShadow: "6px 6px 0 color-mix(in oklab, var(--ink) 18%, transparent)",
      }}
    >
      <p className="tanker" style={{ fontSize: "clamp(24px, 3vw, 32px)", margin: "0 0 4px", lineHeight: 1.15 }}>
        {(top.pct + second.pct).toFixed(0)}% of the time someone hand-picks a model, it's{" "}
        <span style={{ color: STATUS_META[top.status].color }}>{top.name}</span> or{" "}
        <span style={{ color: STATUS_META[second.status].color }}>{second.name}</span>.
      </p>
      <p className="mono" style={{ fontSize: 11, opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 26px" }}>
        source: Quarkle production usage, chat &amp; brainstorm · Jul 24–Aug 3, 2026 · 4,535 explicit
        picks across 164 conversations
      </p>

      <div style={{ display: "flex", width: "100%", height: 34, borderRadius: 8, overflow: "hidden", border: "1.5px solid var(--ink)" }}>
        {sorted.map((m) => (
          <div
            key={m.name}
            title={`${m.name}: ${m.pct}%`}
            style={{
              width: `${m.pct}%`,
              minWidth: 2,
              background: STATUS_META[m.status].color,
              borderRight: "2px solid var(--paper-3)",
            }}
          />
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 18px", marginTop: 16 }}>
        {sorted.map((m) => (
          <div key={m.name} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: STATUS_META[m.status].color, flexShrink: 0 }} />
            <span className="mono" style={{ fontSize: 11.5 }}>
              {m.name} <span style={{ opacity: 0.55 }}>{m.pct}%</span>
            </span>
            {m.tag && (
              <span className="mono" style={{ fontSize: 8, color: "var(--clay)", border: "1px solid var(--clay)", borderRadius: 999, padding: "1px 6px" }}>
                {m.tag}
              </span>
            )}
          </div>
        ))}
      </div>

      <p className="mono" style={{ fontSize: 11, opacity: 0.5, margin: "18px 0 0" }}>
        excludes "Auto" turns · Sonnet 5 / GPT-5.6 Terra shipped Aug 1, thin data
      </p>
    </div>
  );
}

export default function BestModelsPage() {
  const [theme, setTheme] = useState("paper");
  useEffect(() => {
    try {
      const s = window.localStorage.getItem("qv6-theme");
      if (s === "paper" || s === "midnight") setTheme(s);
    } catch (_) {}
  }, []);

  return (
    <div className={`qv6 ${theme === "midnight" ? "theme-midnight" : ""}`}>
      <Nav />

      {/* Hero */}
      <section style={{ padding: "80px 40px 50px", textAlign: "center" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <CornerPin>the real leaderboards</CornerPin>
          <h1
            className="tanker"
            style={{ fontSize: "clamp(48px, 7.5vw, 108px)", lineHeight: 0.94, letterSpacing: "-0.035em", margin: "16px 0 18px" }}
          >
            which ai actually{" "}
            <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>writes well</span>?
          </h1>
          <p className="mono" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.6, marginBottom: 14 }}>
            ranked against real benchmarks, not our opinion
          </p>
          <p className="display" style={{ fontSize: 19, fontStyle: "italic", opacity: 0.85, lineHeight: 1.6, maxWidth: 680, margin: "0 auto" }}>
            most "best AI for writing" pages guess, or make up numbers. we pulled the actual
            leaderboards instead: where our models rank out of the field, on the benchmarks
            that map to how you actually use Quarkle. where no real benchmark exists, we say so.
          </p>
        </div>
      </section>

      {/* Trust strip */}
      <section style={{ padding: "10px 40px 60px" }}>
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "28px 44px",
            textAlign: "center",
          }}
        >
          {[
            ["339", "models scanned across 4 leaderboards to find ours"],
            ["8", "models Quarkle offers, all charted"],
            ["4,535", "real usage picks analyzed, not survey data"],
            ["0", "numbers we made up"],
          ].map(([n, label]) => (
            <div key={label}>
              <div className="tanker" style={{ fontSize: 34, lineHeight: 1, color: "var(--pink)" }}>{n}</div>
              <div className="mono" style={{ fontSize: 11, opacity: 0.55, marginTop: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </section>

      <WaveRule w={200} h={12} style={{ margin: "0 auto 60px", display: "block" }} />

      {/* Charts */}
      <section style={{ padding: "0 40px 60px" }}>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <span className="caps" style={{ color: "var(--pink)" }}>ranked by real benchmark, not vibes</span>
          <h2 className="tanker" style={{ fontSize: "clamp(38px, 4.8vw, 68px)", margin: "10px 0 0", lineHeight: 1 }}>
            six questions with <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>real answers</span>.
          </h2>
        </div>
        <Legend />
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: 28 }}>
          <RankChart data={PROSE_QUALITY} />
          <RankChart data={BRAINSTORMING} />
          <RankChart data={AI_SOUNDING} />
          <RankChart data={CRITIQUE_JUDGING} />
          <RankChart data={SUSTAINED_QUALITY} />
          <RankChart data={WORLD_BUILDING} />
        </div>
      </section>

      {/* Real Quarkle usage, not a benchmark */}
      <section style={{ padding: "40px 40px 100px", background: "var(--paper-2)", borderTop: "1.5px solid var(--ink)", borderBottom: "1.5px solid var(--ink)" }}>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <span className="caps" style={{ color: "var(--violet)" }}>not a benchmark, actual usage</span>
          <h2 className="tanker" style={{ fontSize: "clamp(34px, 4.2vw, 56px)", margin: "10px 0 0", lineHeight: 1.02 }}>
            what quarkle writers <span className="display" style={{ fontStyle: "italic", color: "var(--violet)" }}>actually pick</span>.
          </h2>
        </div>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: 28 }}>
          <UsageShareBar />
        </div>
      </section>

      {/* Methodology / sourcing */}
      <section style={{ padding: "90px 40px 100px", position: "relative" }}>
        <div aria-hidden style={{ position: "absolute", top: 50, right: 50, opacity: 0.5, pointerEvents: "none" }}>
          <Asterism size={24} color="var(--ink)" />
        </div>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <Ribbon color="var(--ink)" style={{ marginBottom: 16 }}>
            how we built this
          </Ribbon>
          <p style={{ fontSize: 15.5, lineHeight: 1.7, opacity: 0.85, margin: 0 }}>
            Every number on this page came from a live leaderboard we pulled ourselves, not a
            summary, not a cached quote: EQ-Bench's Creative Writing v3, Longform, and Judgemark v4
            boards plus fiction.liveBench's long-context eval, fetched {SOURCE_DATE} and linked above
            each chart. A few caveats worth keeping: the writing-quality boards are judged by a Claude
            model (Sonnet 4.6), so Claude's own scores there deserve a raised eyebrow; Judgemark
            measures something different, it scores a model's own judging ability, not another
            judge's opinion of it, so that caveat doesn't apply there; Grok 4.3 is missing from most
            boards, appearing only on Judgemark so far; and fiction.liveBench has only tested one of
            our 8 models. Gaps in the data, not evidence anything writes worse. Check the links
            yourself; they're the same ones we used. The grayed-out bar on some charts is the actual
            #1 model on that specific board, not currently offered in Quarkle. We're showing it so
            this stays an honest leaderboard, not just a lineup of our own models.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "40px 40px 120px", textAlign: "center" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <span className="caps" style={{ color: "var(--pink)" }}>so, which model should you use?</span>
          <h2 className="tanker" style={{ fontSize: "clamp(36px, 5vw, 64px)", margin: "10px 0 18px", lineHeight: 1.02 }}>
            honestly? <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>whichever one</span> fits the scene.
          </h2>
          <p style={{ fontSize: 16.5, lineHeight: 1.65, opacity: 0.85, marginBottom: 32 }}>
            The data above doesn't point at one clean winner, so we didn't build Quarkle around one.
            Pick a model per chapter, switch mid-book, or leave it on auto and let it choose. Free
            to try, no card required.
          </p>
          <StartWritingButton size="xl" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
